import { motion } from "framer-motion";
import type { MarkdownPost } from "../utils/data";
import { Link } from "../router";
import { Layers } from 'lucide-react';
import { ALL_ICONS } from './Icons';

interface ProjectCardProps {
    project: MarkdownPost;
    index: number;
}

export default function ProjectCard({ project, index }: ProjectCardProps) {
    const { title, desc, tags, gradient, icon } = project.frontmatter;
    const IconComponent = (icon && ALL_ICONS[icon]) || Layers;

    return (
        <Link to={`/projects/${project.slug}`}>
            <motion.div
                className="group relative flex flex-col h-full bg-[var(--card-bg)] border-2 border-[#2A2A2A] rounded-2xl overflow-hidden cursor-pointer hover:border-[#404040] transition-all duration-300 shadow-lg hover:shadow-xl"
            >
                {/* Visual Header - Gradient Area matching Reference */}
                <div className="h-40 w-full relative overflow-hidden">
                    {/* Gradient & Noise Group - Masked for Ultra-Smooth Fade */}
                    <div
                        className="absolute inset-0"
                        style={{
                            maskImage: 'linear-gradient(to bottom, black 0%, transparent 100%)',
                            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, transparent 100%)'
                        }}
                    >
                        {/* Active Gradient - defined by project frontmatter (HEX VALUES) */}
                        <div
                            className="absolute inset-0 opacity-50 group-hover:opacity-70 transition-opacity duration-500"
                            style={{
                                backgroundImage: `linear-gradient(to bottom, ${gradient || '#1f2937, #111827'})`
                            }}
                        />

                        {/* OPTIMIZED: Single Watermark Icon instead of 96-node loop */}
                        <div className="absolute -right-8 -bottom-10 opacity-[0.07] group-hover:opacity-[0.15] transition-all duration-500 transform rotate-[-15deg] group-hover:scale-110 group-hover:rotate-[-10deg]">
                            <IconComponent size={200} className="text-white" />
                        </div>

                        {/* Subtle Grid Lines (CSS only, 0 nodes) */}
                        <div
                            className="absolute inset-0 opacity-20"
                            style={{
                                backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)',
                                backgroundSize: '24px 24px'
                            }}
                        />

                        {/* Noise & Texture Overlay - More Visible (Crystalize) */}
                        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-40 mix-blend-overlay" />
                    </div>
                </div>

                {/* Content - Compact Layout */}
                <div className="p-5 flex flex-col flex-grow -mt-2 relative z-10">
                    <h3 className="text-xl font-bold text-white mb-2 leading-tight group-hover:text-[var(--accent)] transition-colors">
                        {title}
                    </h3>

                    <p className="text-[#888] text-sm leading-relaxed mb-4 line-clamp-2">
                        {desc}
                    </p>

                    {/* Footer: Tags & Read More */}
                    <div className="mt-auto flex justify-between items-end gap-2">
                        {/* Tags (Left) */}
                        <div className="flex flex-wrap gap-2">
                            {tags?.slice(0, 3).map((tag: string, i: number) => (
                                <span
                                    key={i}
                                    className="px-2 py-1 text-[10px] font-medium text-gray-400 bg-[#151515] border border-[#333] rounded-md group-hover:border-[#444] transition-colors"
                                >
                                    {tag}
                                </span>
                            ))}
                            {tags?.length > 3 && (
                                <span className="px-2 py-1 text-[10px] text-gray-500 rounded-md">
                                    +{tags.length - 3}
                                </span>
                            )}
                        </div>

                        {/* Read More Link (Right) */}
                        <span className="text-[10px] md:text-[11px] font-bold text-[var(--text-muted)] group-hover:text-white group-hover:underline underline-offset-4 decoration-[var(--accent)] transition-all duration-200 whitespace-nowrap shrink-0">
                            Devamını Oku
                        </span>
                    </div>
                </div>
            </motion.div>
        </Link>
    );
}
